// src/components/awards/AwardsPage.js
import React, { useMemo, useState, memo } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import { Visibility as VisibilityIcon } from "@mui/icons-material";

import {
  useGetPublicAwardsQuery,
  useGetScholarshipEnumsQuery,
} from "../../services/public-awards";
import { useGetCitiesQuery } from "../../services/lookups";

import AwardFormModal from "./AwardFormModal";
import AwardSelectionInfoModal from "./AwardSelectionInfoModal";

const LS_AWARDS_PAGE_FILTERS = "public_awards_page_filters";

const getAwardDisplayName = (row) => {
  const line1 = row?.scholarship_name || row?.name || "";
  const line2 = row?.scholarship_name_line_2 || row?.name_line_2 || "";

  return line2 ? `${line1} - ${line2}` : line1;
};

const AwardsPage = () => {
  const getInitialFilters = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_AWARDS_PAGE_FILTERS));
      return saved || {};
    } catch {
      return {};
    }
  };

  const initialFilters = getInitialFilters();
  const [openModal, setOpenModal] = useState(false);
  const [selectedAward, setSelectedAward] = useState(null);

  const [nameFilter, setNameFilter] = useState(initialFilters.nameFilter ?? "");
  const [descriptionFilter, setDescriptionFilter] = useState(
    initialFilters.descriptionFilter ?? ""
  );
  const [selectedAwardType, setSelectedAwardType] = useState(
    initialFilters.selectedAwardType ?? "All"
  );
  const [selectedCity, setSelectedCity] = useState(initialFilters.selectedCity ?? "");
  const [selectedByFilter, setSelectedByFilter] = useState(
    initialFilters.selectedByFilter ?? "0"
  );
  const [paginationModel, setPaginationModel] = useState({
    page: initialFilters.page ?? 0,
    pageSize: initialFilters.pageSize ?? 50,
  });

  const [selectedAwardIndex, setSelectedAwardIndex] = useState(null);
  const [openSelectionInfo, setOpenSelectionInfo] = useState(false);

  const { data: citiesData } = useGetCitiesQuery();
  const { data: enumsData } = useGetScholarshipEnumsQuery();

  const { data, error, isLoading } = useGetPublicAwardsQuery({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    name: nameFilter,
    description: descriptionFilter,
    awardType: selectedAwardType,
    cityId: selectedCity,
    selectedBy: selectedByFilter,
  });
  // console.log("data", data);
  const rows = data?.data || [];

  React.useEffect(() => {
    localStorage.setItem(
      LS_AWARDS_PAGE_FILTERS,
      JSON.stringify({
        nameFilter,
        descriptionFilter,
        selectedAwardType,
        selectedCity,
        selectedByFilter,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      })
    );
  }, [
    nameFilter,
    descriptionFilter,
    selectedAwardType,
    selectedCity,
    selectedByFilter,
    paginationModel,
  ]);

  const openAwardModal = (award) => {
    const index = rows?.findIndex((r) => r?.id === award?.id);
    setSelectedAwardIndex(index);
    setSelectedAward(award);
    setOpenModal(true);
  };

  const columns = useMemo(
    () => [
      // { field: "id", headerName: "ID", width: 75 },
      {
        field: "actions",
        headerName: "View",
        width: 55,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openAwardModal(params?.row);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </>
        ),
      },
      {
        field: "scholarship_name",
        headerName: "Award",
        width: 420,
        valueGetter: (value, row) => getAwardDisplayName(row),
      },
      {
        field: "award_amount",
        headerName: "Amount",
        width: 85,
        type: "number",
        valueGetter: (value, row) =>
          row?.award_amount ? `$${row?.award_amount}` : "",
      },
      {
        field: "number_of_awards",
        width: 85,
        headerAlign: "center",
        align: "center",
        renderHeader: () => (
          <Box sx={{ whiteSpace: "pre-wrap", textAlign: "center", width: '100%' }}>
            {"Number of\nAwards"}
          </Box>
        ),
      },

      {
        field: "award_type",
        width: 75,
        renderHeader: () => (
          <Box sx={{ whiteSpace: "pre-wrap", textAlign: "center" }}>
            {"Award \nType"}
          </Box>
        ),
      },
      {
        field: "selected_by_name",
        width: 150,
        renderHeader: () => (
          <Box sx={{ whiteSpace: "pre-wrap", textAlign: "center" }}>
            Selected By
          </Box>
        ),
      },
      {
        field: "presented_at_name",
        width: 300,
        renderHeader: () => (
          <Box sx={{ whiteSpace: "pre-wrap", textAlign: "center" }}>
            Presented At
          </Box>
        ),
      },

      {
        field: "affiliations",
        headerName: "Affiliations",
        width: 160,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            {params.row.affiliations ? params.row.affiliations : ""}
          </Typography>
        ),
      },
      {
        field: "requirements",
        headerName: "Requirements",
        width: 200,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            {params.row.requirements ? params.row.requirements : ""}
          </Typography>
        ),
      },
      {
        field: "documents",
        headerName: "Additional Application",
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const docs = Array.isArray(params.row.documents)
            ? params.row.documents.filter((doc) => doc.type === "public")
            : [];
          return (
            <Box>
              {docs.length > 0 ? (
                <Box>
                  {docs.map((doc) => (
                    <a
                      key={doc.id}
                      href={
                        doc.view_url || doc.url || doc.s3Url || doc.downloadUrl
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#1976d2",
                        textDecoration: "underline",
                      }}
                    >
                      {doc.file_name || doc.name || doc.filename || "Document"}
                    </a>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  &nbsp;
                </Typography>
              )}
            </Box>
          );
        },
      },
    ],
    [rows] // eslint-disable-line
  );

  const handleRowClick = (row) => {
    const index = rows?.findIndex((r) => r?.id === row?.id);
    setSelectedAward(row);
    setSelectedAwardIndex(index);
  };

  const handlePreviousAward = () => {
    if (selectedAwardIndex > 0) {
      const newIndex = selectedAwardIndex - 1;
      setSelectedAward(rows[newIndex]);
      setSelectedAwardIndex(newIndex);
    }
  };

  const handleNextAward = () => {
    if (selectedAwardIndex < rows?.length - 1) {
      const newIndex = selectedAwardIndex + 1;
      setSelectedAward(rows[newIndex]);
      setSelectedAwardIndex(newIndex);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAward(null);
    setSelectedAwardIndex(null);
  };

  const canGoPrevious = selectedAwardIndex !== null && selectedAwardIndex > 0;
  const canGoNext =
    selectedAwardIndex !== null && selectedAwardIndex < rows?.length - 1;

  const handleResetFilters = () => {
    setNameFilter("");
    setDescriptionFilter("");
    setSelectedAwardType("All");
    setSelectedCity("");
    setSelectedByFilter("0");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    localStorage.removeItem(LS_AWARDS_PAGE_FILTERS);
  };

  return (
    <Box sx={{ width: "100%", padding: "1rem" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" gutterBottom>
          Awards
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        {/* Name Filter */}
        <TextField
          label="Name"
          size="small"
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />
        {/* Description Filter */}
        <TextField
          label="Description"
          size="small"
          value={descriptionFilter}
          onChange={(e) => {
            setDescriptionFilter(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />
        {/** Award Type Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Award Type</InputLabel>
          <Select
            value={selectedAwardType}
            label="Award Type"
            onChange={(e) => {
              setSelectedAwardType(e.target.value);
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
          >
            <MenuItem value="All">
              <em>All</em>
            </MenuItem>
            <MenuItem value="Award">Award</MenuItem>
            <MenuItem value="Bursary">Bursary</MenuItem>
            <MenuItem value="Scholarship">Scholarship</MenuItem>
          </Select>
        </FormControl>
        {/* City Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>City</InputLabel>
          <Select
            value={selectedCity}
            label="City"
            onChange={(e) => {
              setSelectedCity(e.target.value);
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
          >
            <MenuItem value="">
              <em>All Cities</em>
            </MenuItem>
            {citiesData?.data?.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Selected By Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Selected By</InputLabel>
          <Select
            value={selectedByFilter}
            label="Selected By"
            onChange={(e) => {
              setSelectedByFilter(e.target.value);
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
          >
            <MenuItem value="0">
              <em>All</em>
            </MenuItem>
            {enumsData?.data?.selectedBy?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={handleResetFilters}>
          Reset Filters
        </Button>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: "70%",
          padding: "1rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Tooltip title="Click to view how award selections are made">
          <Button variant="text" onClick={() => setOpenSelectionInfo(true)}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              How are award selections made?
            </Typography>
          </Button>
        </Tooltip>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: "70%",
          padding: "1rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body1" sx={{ textWrap: "wrap" }}>
          <strong>Please Note:</strong> Awards Selected By Donor require an
          affiliation and/or additional information as described in the award
          details below. Click on each award for award details.
        </Typography>
      </Box>
      {isLoading ? (
        <Box textAlign="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box textAlign="center" mt={2}>
          <Typography color="error">Error loading awards data.</Typography>
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row?.id}
          getRowHeight={() => "auto"}
          autoHeight
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={data?.total || 0}
          pageSizeOptions={[50, 100]}
          onRowClick={handleRowClick}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f5f5f5",
            },
            "& .MuiDataGrid-filler": {
              backgroundColor: "#f5f5f5",
            },
          }}
        />
      )}
      {openModal && (
        <AwardFormModal
          open={openModal}
          onClose={handleCloseModal}
          awardData={selectedAward}
          onPrevious={handlePreviousAward}
          onNext={handleNextAward}
          disablePrevious={!canGoPrevious}
          disableNext={!canGoNext}
        />
      )}
      <AwardSelectionInfoModal
        open={openSelectionInfo}
        onClose={() => setOpenSelectionInfo(false)}
      />
    </Box>
  );
};

export default memo(AwardsPage);
